import { Component, Vue } from "vue-property-decorator";
import iziToast from "izitoast";
import adtPagination from "@/components/communs/Pagination.vue";
import Spinner from "@/components/Spinner.vue";
import Helpers from "@/helpers/helpers";
import SearchBox from "@/components/SearchBox.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import AdtModalContent from "@/components/AdtModalContent.vue";
import dayjs from "dayjs";
import {
  EventsRequest,
  EventsResponse,
  PublishStatus,
  ReprocessEvent,
} from "@/models/Events/events.interface";

@Component({
  components: {
    adtPagination,
    Spinner,
    "adt-search-box": SearchBox,
    "status-badge": StatusBadge,
    "adt-modal-content": AdtModalContent,
  },
})
export default class EventsList extends Vue {
  private isLoading = false;
  private events: EventsResponse[] = [];
  private total = 0;
  private parameters: EventsRequest = {
    eventName: "",
    source: "",
    keywordSearch: "",
    publishStatus: "",
    startDate: "",
    endDate: "",
    pageSize: 10,
  };
  private currentEvent: EventsResponse = {
    id: "",
    eventSource: "",
    eventName: "",
    addedAtUtc: "",
    lastUpdateDateUtc: "",
    consumers: [],
    event: {
      _t: "",
      _id: "",
      CreationDate: "",
      Source: "",
      TraceKey: "",
      EventSuffix: "",
      Contents: "",
      OriginalFile: {
        _id: "",
        FileName: "",
        Metadata: {
          header: "",
          trailer: "",
          contentHeader: "",
          contentTrailer: "",
          source: 0,
          acquirerId: 0,
        },
      },
    },
    publishStatus: 0,
    consumptionStatus: "",
  };
  private isContentCopied = false;

  private range: any = {
    start: dayjs(new Date()).subtract(60, "day").toDate(),
    end: new Date(),
  };

  get spinnerColor() {
    return getComputedStyle(document.documentElement).getPropertyValue("--secondary-color");
  }

  private getPublishStatus() {
    return Helpers.eventPublishedStatus;
  }

  private getConsumptionStatus() {
    return Helpers.eventConsumptionStatus;
  }

  private created() {
    this.$route.query.currentPage !== undefined && this.$route.query.currentPage > "1"
      ? (this.parameters.currentPage = Number(this.$route.query.currentPage))
      : (this.parameters.currentPage = 1);

    if (this.$route.query.eventName !== undefined) {
      this.parameters.eventName = this.$route.query.eventName as string;
    }

    if (this.$route.query.source !== undefined) {
      this.parameters.source = this.$route.query.source as string;
    }

    if (this.$route.query.keywordSearch !== undefined) {
      this.parameters.keywordSearch = this.$route.query.keywordSearch as string;
    }

    if (this.$route.query.publishStatus !== undefined) {
      const publishStatus = this.$route.query.publishStatus as string;
      if (Object.keys(PublishStatus).some((col: string) => col === publishStatus)) {
        this.parameters.publishStatus = publishStatus as unknown as PublishStatus;
      }
    }

    if (this.$route.query.startDate !== undefined) {
      //@ts-ignore
      this.range.start = dayjs(this.$route.query.startDate).toDate();
      //@ts-ignore
      this.range.end = dayjs(this.$route.query.endDate).toDate();
      this.parameters.startDate = dayjs(this.range.start).format("YYYY-MM-DD");
      this.parameters.endDate = dayjs(this.range.end).format("YYYY-MM-DD");
    } else {
      this.parameters.startDate = dayjs(this.range.start).format("YYYY-MM-DD");
      this.parameters.endDate = dayjs(this.range.end).format("YYYY-MM-DD");
    }   

    this.parameters = {
      ...this.parameters,
      ...this.$route.query,
    };

    this.addParamsToQuerystring(this.parameters);
    this.getEvents(this.parameters);
  }

  private addParamsToQuerystring(parameters: EventsRequest) {
    //@ts-ignore
    this.$router.replace({ ...this.$router.currentRoute, query: parameters }).catch(() => {});
  }

  @Helpers.Debounce(800)
  private searchEvent(value: string) {
    this.parameters.eventName = value;
    this.onSearchBox();
  }

  @Helpers.Debounce(800)
  private searchSource(value: string) {
    this.parameters.source = value;
    this.onSearchBox();
  }

  @Helpers.Debounce(800)
  private searchKeyword(value: string) {
    this.parameters.keywordSearch = value;
    this.onSearchBox();
  }

  @Helpers.Debounce(800)
  private searchPublishStatus(value: PublishStatus) {
    this.parameters.publishStatus = value;
    this.onSearchBox();
  }

  private onSearchBox() {
    this.changePage(1);
    this.addParamsToQuerystring(this.parameters);
    this.getEvents(this.parameters);
  }

  OnDatePicker(payload: any) {
    if (payload !== null) {
      const payloadStart = dayjs(payload.start).startOf("day").format("YYYY-MM-DD");
      const payloadEnd = dayjs(payload.end).endOf("day").format("YYYY-MM-DD");
      this.parameters.startDate = payloadStart;
      this.parameters.endDate = payloadEnd;
      this.onSearchBox();
    }
  }

  private clearFilter() {
    this.range.start = dayjs(new Date()).subtract(60, "day").toDate();
    this.range.end = new Date();
    this.parameters = {
      eventName: "",
      source: "",
      keywordSearch: "",
      publishStatus: "",
      startDate: dayjs(this.range.start).format("YYYY-MM-DD"),
      endDate: dayjs(this.range.end).format("YYYY-MM-DD"),
      currentPage: 1,
      pageSize: 10,
    };
    this.addParamsToQuerystring(this.parameters);
    this.getEvents(this.parameters);
  }

  private changePage(page: number) {
    this.parameters.currentPage = page;
    this.addParamsToQuerystring(this.parameters);
    this.getEvents(this.parameters);
  }

  private perPage(e: number) {
    this.parameters.pageSize = e;
    this.getEvents(this.parameters);
  }

  private toggleContentModal(name: string, event: EventsResponse) {
    this.currentEvent = event;
    // @ts-ignore
    this.$refs[name].toggle();
  }

  private copyContent(elementId: string) {
    Helpers.copyDivContentOnClick(elementId);
    this.isContentCopied = true;

    setTimeout(() => {
      if (this.isContentCopied === true) this.isContentCopied = false;
    }, 2000);
  }

  private formatContentOnDisplay(content: string) {
    if (content) {
      return JSON.stringify(content).substring(0, 50) + "...";
    }
  }

  private getEvents(params: EventsRequest) {
    this.isLoading = true;

    this.$store
      .dispatch("events/GET_EVENTS", {
        eventName: params.eventName,
        source: params.source,
        keywordSearch: params.keywordSearch,
        publishStatus: params.publishStatus,
        startDate: params.startDate,
        endDate: params.endDate,
        currentPage: params.currentPage,
        pageSize: params.pageSize,
      })
      .then((result: any) => {
        this.events = result.data.events;
        this.total = result.data.total;
      })
      .catch((err) => {
        iziToast.error({
          title: "Erro:",
          message: err,
          position: "bottomRight",
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private reprocessEvent() {
    this.isLoading = true;
    const body: ReprocessEvent = {
      eventId: this.currentEvent.id,
      type: 1,
    };
    this.$store
      .dispatch("events/REPROCESS_EVENT", body)
      .then((res: any) => {
        this.toggleContentModal("eventContentModal", this.currentEvent);
        iziToast.success({
          title: "Evento reprocessado com sucesso!",
          position: "bottomRight",
        });
      })
      .catch((err) => {
        iziToast.error({
          title: "Erro:",
          message: err,
          position: "bottomRight",
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.getEvents(this.parameters);
      });
  }
}
